/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  Visit,
  Note,
  VisitorAgreement,
  DocumentCheck,
  Visitor,
  UpdateVisitPayload,
  CreateVisitNotePayload,
  ProcessVisitorExitPayload,
} from '../../types/visitTypes';
import { ApiResponse } from '../../types/apiTypes';

export interface VisitsState {
  visits: Visit[];
  selectedVisit: Visit | null;
  selectedVisitor: Visitor | null;
  error: string | null;
  notes: ApiResponse<Note>;
  agreements: ApiResponse<VisitorAgreement>;
  documentsCheck: ApiResponse<DocumentCheck>;
  visitsStartDate: string | undefined;
  visitsEndDate: string | undefined;
  visitsDisplayStartDate: string | undefined;
  visitsDisplayEndDate: string | undefined;

  // Loading States
  fetchVisitsLoading: boolean;
  describeVisitLoading: boolean;
  updateVisitLoading: boolean;
  fetchVisitNotesLoading: boolean;
  fetchAgreementsLoading: boolean;
  addVisitNoteLoading: boolean;
  processVisitorExitLoading: boolean;
  checkDocumentsLoading: boolean;
}

const initialState: VisitsState = {
  visits: [],
  selectedVisit: null,
  selectedVisitor: null,
  error: null,
  notes: { data: [], meta: null, totalCount: 0, filteredCount: 0 },
  agreements: { data: [], meta: null, totalCount: 0, filteredCount: 0 },
  documentsCheck: { data: [], meta: null, totalCount: 0, filteredCount: 0 },
  visitsStartDate: undefined,
  visitsEndDate: undefined,
  visitsDisplayStartDate: undefined,
  visitsDisplayEndDate: undefined,
  fetchVisitsLoading: false,
  describeVisitLoading: false,
  updateVisitLoading: false,
  fetchVisitNotesLoading: false,
  fetchAgreementsLoading: false,
  addVisitNoteLoading: false,
  processVisitorExitLoading: false,
  checkDocumentsLoading: false,
};

type VisitsLoadingKey =
  | 'fetchVisitsLoading'
  | 'describeVisitLoading'
  | 'updateVisitLoading'
  | 'fetchVisitNotesLoading'
  | 'fetchAgreementsLoading'
  | 'addVisitNoteLoading'
  | 'processVisitorExitLoading'
  | 'checkDocumentsLoading';

const visitsSlice = createSlice({
  name: 'visitors',
  initialState,
  reducers: {
    fetchVisits(
      state,
      _action: PayloadAction<{
        orgId: number;
        firstName?: string;
        lastName?: string;
        locationId?: number;
        startDate?: string;
        endDate?: string;
      }>,
    ) {
      state.error = null;
    },
    describeVisit(
      state,
      _action: PayloadAction<{ orgId: number; visitId: number }>,
    ) {
      state.error = null;
    },
    updateVisit(
      _state,
      _action: PayloadAction<{
        orgId: number;
        visitId: number;
        visitPayload: Partial<UpdateVisitPayload>;
      }>,
    ) {},
    fetchVisitNotes(
      _state,
      _action: PayloadAction<{ orgId: number; visitId: number }>,
    ) {},
    createVisitNote(
      _state,
      _action: PayloadAction<{
        orgId: number;
        visitId: number;
        visitNote: CreateVisitNotePayload;
      }>,
    ) {},
    fetchVisitorAgreements(
      _state,
      _action: PayloadAction<{
        orgId: number;
        visitId: number;
        documentId?: number;
        visitorId?: number;
      }>,
    ) {},
    processVisitorExit(
      _state,
      _action: PayloadAction<{
        orgId: number;
        visitId: number;
        exitPayload: Partial<ProcessVisitorExitPayload>;
      }>,
    ) {},
    checkDocuments(
      _state,
      _action: PayloadAction<{ orgId: number; visitId: number }>,
    ) {},

    setVisits(state, action: PayloadAction<Visit[]>) {
      state.visits = action.payload;
    },
    setSelectedVisit(state, action: PayloadAction<Visit | null>) {
      state.selectedVisit = action.payload;
    },
    setNotes(state, action: PayloadAction<ApiResponse<Note>>) {
      state.notes = action.payload;
    },
    setAgreements(state, action: PayloadAction<ApiResponse<VisitorAgreement>>) {
      state.agreements = action.payload;
    },
    setDocumentsCheck(
      state,
      action: PayloadAction<ApiResponse<DocumentCheck>>,
    ) {
      state.documentsCheck = action.payload;
    },

    setLoading(
      state,
      action: PayloadAction<{ key: VisitsLoadingKey; value: boolean }>,
    ) {
      state[action.payload.key] = action.payload.value;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },

    setVisitsStartDate(state, action: PayloadAction<string | undefined>) {
      state.visitsStartDate = action.payload;
    },
    setVisitsEndDate(state, action: PayloadAction<string | undefined>) {
      state.visitsEndDate = action.payload;
    },
    setVisitsDisplayStartDate(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.visitsDisplayStartDate = action.payload;
    },
    setVisitsDisplayEndDate(state, action: PayloadAction<string | undefined>) {
      state.visitsDisplayEndDate = action.payload;
    },
    setSelectedVisitor(state, action: PayloadAction<Visitor | null>) {
      state.selectedVisitor = action.payload;
    },
    clearVisits(state) {
      state.visits = initialState.visits;
    },
    clearSelectedVisit(state) {
      state.selectedVisit = initialState.selectedVisit;
    },
    clearNotes(state) {
      state.notes = initialState.notes;
    },
    clearDocumentsCheck(state) {
      state.documentsCheck = initialState.documentsCheck;
    },
    clearVisitsState() {
      return initialState;
    },
  },
});

export const {
  fetchVisits,
  describeVisit,
  updateVisit,
  fetchVisitNotes,
  createVisitNote,
  fetchVisitorAgreements,
  processVisitorExit,
  checkDocuments,
  setVisits,
  setSelectedVisit,
  setNotes,
  setAgreements,
  setDocumentsCheck,
  setLoading,
  setError,
  setVisitsStartDate,
  setVisitsEndDate,
  setVisitsDisplayStartDate,
  setVisitsDisplayEndDate,
  setSelectedVisitor,
  clearVisits,
  clearSelectedVisit,
  clearNotes,
  clearDocumentsCheck,
  clearVisitsState,
} = visitsSlice.actions;

export const visitsActions = visitsSlice.actions;
export default visitsSlice.reducer;
